import { reportUnexpectedError } from '@/shared/infrastructure/observability/reportUnexpectedError'
import type { ContractRepository } from '../application/ContractRepository'

export function createResilientContractRepository(
  primary: ContractRepository,
  fallback: ContractRepository,
): ContractRepository {
  return {
    async list(filters) {
      try {
        return await primary.list(filters)
      } catch (error) {
        reportUnexpectedError(error, { feature: 'contracts', operation: 'list', fallback: true })
        const result = await fallback.list(filters)
        return {
          ...result,
          meta: {
            ...result.meta,
            source: 'fixture-fallback',
            fallbackReason: 'CONTRACTS_REMOTE_UNAVAILABLE',
          },
        }
      }
    },
    async getByReference(reference) {
      try {
        return await primary.getByReference(reference)
      } catch (error) {
        reportUnexpectedError(error, { feature: 'contracts', operation: 'detail', fallback: true })
        const result = await fallback.getByReference(reference)
        return result
          ? {
              ...result,
              meta: {
                ...result.meta,
                source: 'fixture-fallback',
                fallbackReason: 'CONTRACTS_REMOTE_UNAVAILABLE',
              },
            }
          : null
      }
    },
    // Une écriture ne se replie jamais sur les fixtures.
    createFromQuote(command) {
      return primary.createFromQuote(command)
    },
    listZones(contractId) {
      return primary.listZones(contractId)
    },
    measurementState(contractId) {
      return primary.measurementState(contractId)
    },
    createCaptureUrl(storagePath) {
      return primary.createCaptureUrl(storagePath)
    },
    replaceZones(command) {
      return primary.replaceZones(command)
    },
    attachMeasurementPlan(contractId, revision, image) {
      return primary.attachMeasurementPlan(contractId, revision, image)
    },
    createDraft(command) {
      return primary.createDraft(command)
    },
    updateTerms(command) {
      return primary.updateTerms(command)
    },
    listClientProperties(clientId) {
      return primary.listClientProperties(clientId)
    },
    listServiceLocations(contractId, options) {
      return primary.listServiceLocations(contractId, options)
    },
    configureLocations(command) {
      return primary.configureLocations(command)
    },
    async listActiveTaxRatesPpm() {
      try {
        return await primary.listActiveTaxRatesPpm()
      } catch (error) {
        // Un taux inventé se lirait comme un fait sur un document opposable :
        // le détail de taxes disparaît plutôt que d'être approximé.
        reportUnexpectedError(error, { feature: 'contracts', operation: 'tax-rates', fallback: true })
        return []
      }
    },
    listSchedule(contractId) {
      return primary.listSchedule(contractId)
    },
    replaceSchedule(command) {
      return primary.replaceSchedule(command)
    },
    listSignatures(contractId) {
      return primary.listSignatures(contractId)
    },
    listEvents(contractId) {
      return primary.listEvents(contractId)
    },
    listNotes(contractId) {
      return primary.listNotes(contractId)
    },
    createNote(command) {
      return primary.createNote(command)
    },
    updateNote(command) {
      return primary.updateNote(command)
    },
    softDeleteNote(noteId) {
      return primary.softDeleteNote(noteId)
    },
    recordSignature(command) {
      return primary.recordSignature(command)
    },
    recordManualSignature(command) {
      return primary.recordManualSignature(command)
    },
    activateNoInvoice(command) {
      return primary.activateNoInvoice(command)
    },
    changeStatus(command) {
      return primary.changeStatus(command)
    },
    regularize(contractId) {
      return primary.regularize(contractId)
    },
    markNotInterested(contractIds) {
      return primary.markNotInterested(contractIds)
    },
    revertToSignature(contractId) {
      return primary.revertToSignature(contractId)
    },
    listAmendments(contractId) {
      // Aucun repli : une liste d'avenants inventée ferait croire à un
      // engagement modifié qui n'existe nulle part.
      return primary.listAmendments(contractId)
    },
    createAmendment(command) {
      return primary.createAmendment(command)
    },
    applyAmendment(amendmentId) {
      return primary.applyAmendment(amendmentId)
    },
    archiveDocument(command) {
      return primary.archiveDocument(command)
    },
    sendDocument(command) {
      return primary.sendDocument(command)
    },
    generateInvoices(command) {
      return primary.generateInvoices(command)
    },
    async listPendingClient() {
      try {
        return await primary.listPendingClient()
      } catch (error) {
        reportUnexpectedError(error, { feature: 'contracts', operation: 'pending-client', fallback: true })
        const result = await fallback.listPendingClient()
        return {
          ...result,
          meta: {
            ...result.meta,
            source: 'fixture-fallback',
            fallbackReason: 'CONTRACTS_REMOTE_UNAVAILABLE',
          },
        }
      }
    },
    scheduleFollowUp(command) {
      return primary.scheduleFollowUp(command)
    },
    softDelete(contractId) {
      return primary.softDelete(contractId)
    },
    restore(contractId) {
      return primary.restore(contractId)
    },
    listDeleted(filters) {
      return primary.listDeleted(filters)
    },
    geocodeAddress(command) {
      return primary.geocodeAddress(command)
    },
    getBillingState(contractId) {
      return primary.getBillingState(contractId)
    },
    reviseBilling(command) {
      return primary.reviseBilling(command)
    },
    updateAddress(command) {
      return primary.updateAddress(command)
    },
    setMarkersRequired(command) {
      return primary.setMarkersRequired(command)
    },
    setOverviewHidden(contractId, hidden) {
      return primary.setOverviewHidden(contractId, hidden)
    },
    setServiceLocationStatus(locationId, status) {
      return primary.setServiceLocationStatus(locationId, status)
    },
  }
}
